import React, { useEffect, useState } from 'react';
import { useMarketplace, useNFTCollection } from '@thirdweb-dev/react';
import { NATIVE_TOKEN_ADDRESS } from '@thirdweb-dev/sdk';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';

import CancelSvg from 'assets/icons/modal-close.svg';
import { Button, Input } from 'components/common';
import EthSvg from 'assets/icons/priceIcon.svg';
import axiosData from 'services/fetchInterceptor';
import { ethers } from 'ethers';
import propertyService from 'services/propertyService';
import { useToast } from '../Toast/ToastProvider';

interface AddFeatureProps {
  showModal: boolean;
  setShowModal: any;
  property: any;
  fetchProperty: (slug: string) => void;
}

const EnterPricePropertyModal = ({
  showModal,
  setShowModal,
  property,
  fetchProperty,
}: AddFeatureProps) => {
  const toast = useToast();

  // Connect your marketplace smart contract here (replace this address)
  const marketplace = useMarketplace(
    process.env.REACT_APP_MARKETPLACE_ADDRESS // Your marketplace contract address here
  );

  const CollectionAddress = process.env.REACT_APP_COLLECTION_ADDRESS!;
  const [loading, setLoading] = useState(false);

  const ListProperty = async (
    isDirectListing: boolean,
    minPrice: any,
    tokenId: any,
    endDate: any,
    maxPrice?: any
  ) => {
    try {
      setLoading(true);
      let tx;

      const date = new Date(endDate);

      const endDateInSeconds = Math.floor(date.getTime() / 1000);

      if (isDirectListing) {
        tx = await marketplace?.direct.createListing({
          assetContractAddress: CollectionAddress, // Contract Address of the NFT
          buyoutPricePerToken: minPrice, // Maximum price, the auction will end immediately if a user pays this price.
          currencyContractAddress: NATIVE_TOKEN_ADDRESS, // NATIVE_TOKEN_ADDRESS is the crpyto curency that is native to the network. e.g. ETH.
          listingDurationInSeconds: endDateInSeconds, // When the auction will be closed and no longer accept bids (1 Week)
          quantity: 1, // How many of the NFTs are being listed (useful for ERC 1155 tokens)
          startTimestamp: new Date(0), // When the listing will start
          tokenId, // Token ID of the NFT.
        });
      } else {
        tx = await marketplace?.auction.createListing({
          assetContractAddress: CollectionAddress, // Contract Address of the NFT
          buyoutPricePerToken: maxPrice, // Maximum price, the auction will end immediately if a user pays this price.
          currencyContractAddress: NATIVE_TOKEN_ADDRESS, // NATIVE_TOKEN_ADDRESS is the crpyto curency that is native to the network. e.g. ETH.
          listingDurationInSeconds: endDateInSeconds, // When the auction will be closed and no longer accept bids (1 Week)
          quantity: 1, // How many of the NFTs are being listed (useful for ERC 1155 tokens)
          reservePricePerToken: minPrice, // Minimum price, users cannot bid below this amount
          startTimestamp: new Date(), // When the listing will start
          tokenId, // Token ID of the NFT.
        });
      }

      const receipt = tx?.receipt; // the transaction receipt
      let listingId: any = tx?.id;

      listingId = ethers.BigNumber.from(listingId).toNumber();

      if (tx) {
        const res = await propertyService.listProperty(property?.propertyId, {
          price: minPrice,
          maxPrice,
          listingId,
          isDirectListing,
        });

        if (res) {
          toast?.success(`${property.title} listed successfully`);
          fetchProperty(property.slug);
          setLoading(false);
          setShowModal(false);
        }
      }
    } catch (error) {
      toast?.error('An error occurred');
      setLoading(false);
      console.log(error);
    }
  };
  const currentDate = new Date();
  const PricingSchema = Yup.object().shape({
    minPrice: Yup.number().required().positive(),
    maxPrice: Yup.number().positive(),
    endDate: Yup.date().min(currentDate, 'End date cannot be today'),
  });

  return (
    <>
      {showModal ? (
        <>
          <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
            <div className="relative my-6 mx-auto w-[36rem] max-w-xl">
              <div className="bg-gray-4 border-0 rounded-lg shadow-lg relative flex flex-col w-full outline-none focus:outline-none">
                <div className="flex items-start justify-between p-5 border-b border-solid border-slate-200 rounded-t">
                  <h3 className="text-white text-2xl font-semibold">
                    List your asset
                  </h3>
                  <button
                    className="p-1 ml-auto bg-transparent border-0 text-black  float-right text-3xl leading-none font-semibold outline-none focus:outline-none"
                    onClick={() => setShowModal(false)}
                  >
                    <img src={CancelSvg} alt="close-modal" />
                  </button>
                </div>
                <div className="">
                  <Formik
                    initialValues={{
                      minPrice: '',
                      maxPrice: '',
                      isDirectListing: false,
                      endDate: '',
                    }}
                    validationSchema={PricingSchema}
                    onSubmit={(values, actions) => {
                      console.log(values, actions);
                      ListProperty(
                        values.isDirectListing,
                        values.minPrice,
                        property.tokenId,
                        values.endDate,
                        values.maxPrice
                      );
                    }}
                  >
                    {(props) => {
                      const {
                        values,
                        errors,
                        isSubmitting,
                        touched,
                        setFieldValue,
                      } = props || {};

                      return (
                        <Form>
                          <div className="relative p-6 flex-auto">
                            <div className="grid grid-cols-3 mt-4">
                              <div className="">
                                <p className="text-white opacity-70">
                                  {!values.isDirectListing
                                    ? 'Minimum bid'
                                    : 'Property'}
                                  price ::{' '}
                                </p>
                              </div>
                              <div className="col-span-2 flex">
                                <img
                                  src={EthSvg}
                                  alt="eth-price"
                                  className="mr-2"
                                />
                                <Input
                                  name="minPrice"
                                  value={values.minPrice}
                                  type="number"
                                  error={errors?.minPrice}
                                  {...props}
                                  className="bg-transparent border border-white px-2 py-1 rounded text-white outline-none appearance-none hover:appearance-none"
                                />
                                <p className="text-white ml-2">
                                  <small>(750,000 USDC)</small>{' '}
                                </p>
                              </div>
                            </div>
                            {!values.isDirectListing && (
                              <div className="grid grid-cols-3 mt-4">
                                <div className="">
                                  <p className="text-white opacity-70">
                                    Maximum bid price ::{' '}
                                  </p>
                                </div>
                                <div className="col-span-2 flex">
                                  <img
                                    src={EthSvg}
                                    alt="eth-price"
                                    className="mr-2"
                                  />
                                  <Input
                                    name="maxPrice"
                                    value={values.maxPrice}
                                    type="number"
                                    error={errors?.maxPrice}
                                    {...props}
                                    className="bg-transparent border border-white px-2 py-1 rounded text-white outline-none appearance-none hover:appearance-none"
                                  />
                                  <p className="text-white ml-2">
                                    <small>(750,000 USDC)</small>{' '}
                                  </p>
                                </div>
                              </div>
                            )}
                            <div className="grid grid-cols-3 mt-4">
                              <div className="">
                                <p className="text-white opacity-70">
                                  Close listing on ::{' '}
                                </p>
                              </div>
                              <div className="col-span-2 flex">
                                <Input
                                  name="endDate"
                                  value={values.endDate}
                                  type="date"
                                  error={errors?.endDate}
                                  {...props}
                                  className="bg-transparent border border-white px-2 py-1 rounded text-white outline-none appearance-none hover:appearance-none"
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-3">
                              <div className="">
                                <p className="text-white opacity-70">
                                  Negotiable price :{' '}
                                </p>
                              </div>
                              <div className="col-span-2 flex">
                                <input
                                  type="checkbox"
                                  value=""
                                  id="default-toggle"
                                  className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600">
                                  .
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center justify-end p-6 border-t border-solid border-slate-200 rounded-b">
                            <button
                              className="bg-primary-2 text-white active:bg-primary-3 font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                              type="submit"
                              // onClick={ListProperty(1, 1, 1)}
                            >
                              List Asset
                            </button>
                          </div>
                        </Form>
                      );
                    }}
                  </Formik>
                </div>
              </div>
            </div>
          </div>
          <div className="opacity-25 fixed inset-0 z-40 bg-black" />
        </>
      ) : null}
    </>
  );
};

export default EnterPricePropertyModal;
